import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { RowClickedEvent, SelectionChangedEvent } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ChangeEvent, ReactElement } from 'react'

import columns from './columns'
import EditControls from './EditControls'

import AddPropertiesModal from '../modals/AddPropertiesModal'
import ChangeStatusModal from '../modals/ChangeStatusModal'
import CreateAssetModal from '../modals/CreateModal'
import EditAssetModal from '../modals/EditModal'

import Button from '~/components/Buttons/Button'
import ContentLayout from '~/components/ContentLayout'
import Filters, { FiltersContent } from '~/components/Filters'
import SelectInput from '~/components/Inputs/Basic/SelectInput'
import TextInput from '~/components/Inputs/Basic/TextInput'
import Layout from '~/components/Layouts/Layout'
import Section from '~/components/Section'
import ServerSideTableWrapper from '~/components/Tables/ServerSideTableWrapper'
import ViewTitle from '~/components/ViewTitle'
import { AUTHORIZATIONS } from '~/const'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useTablePagination } from '~/hooks/useTablePagination'
import { useTouchDown } from '~/hooks/useTouchDown'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { assetsManagementKeys, useAssetsListQuery, useAssetsTagsKeysQuery } from '~/queries/assets'
import { useUsersListQuery } from '~/queries/users'
import { formatBuilder, isUserId } from '~/utils/builder'
import { removeEmptyAttribute } from '~/utils/object'
import { isFocusedCellInteractive } from '~/utils/table'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const ENTRIES_NAME = 'assets'

enum FILTERS {
  TAG_KEY = 'tag-key:in',
  TAG_VALUE = 'tag-value:like',
}

const DEFAULT_FILTERS = {
  [FILTERS.TAG_KEY]: '',
  [FILTERS.TAG_VALUE]: '',
}

const AssetsListing = (): ReactElement => {
  /** Local state */

  const assetsWriteAuthorized = useIsUserAuthorized([AUTHORIZATIONS.ASSETS_MANAGER.ASSET.WRITE])

  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const gridRef = useRef<AgGridReact>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  const [selectedRows, setSelectedRows] = useState<string[]>([])

  const [selectedRowStatuses, setSelectedRowStatuses] = useState<string[]>([])

  const [isCreateAssetModalOpen, setIsCreateAssetModalOpen] = useState(false)

  const [isUpdateAssetModalOpen, setIsUpdateAssetModalOpen] = useState(false)

  const [isAddPropertiesModalOpen, setIsAddPropertiesModalOpen] = useState(false)

  const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] = useState(false)

  const [assetToEdit, setAssetToEdit] = useState<string>()

  const { isTouchDown, setIsTouchDown } = useTouchDown('Meta')

  const paginationApi = useTablePagination(ENTRIES_NAME, DEFAULT_FILTERS)

  const { filters, orderBy, page, search, setFilters, size } = paginationApi

  const queryParams = useMemo(
    () => ({
      ['order-by']: orderBy,
      page,
      size,
      ...search,
      ...removeEmptyAttribute(filters),
    }),
    [filters, orderBy, page, size, search]
  )

  /** Queries & Mutations */

  const { data: tagKeys } = useAssetsTagsKeysQuery()

  const tagsKeyOptions = tagKeys?.map((key) => ({ label: key, value: key })) || []

  const { data: usersData = [] } = useUsersListQuery()

  const { data: assetsData, isLoading: isAssetsLoading } = useAssetsListQuery(queryParams)

  const rowData = assetsData?.data?.map((assetData) => ({
    ...assetData,
    created_at_ts: assetData.created_at * 1000000,
    created_by: isUserId(assetData.created_by)
      ? usersData.find((user) => user.user_id === assetData.created_by)
      : { name: formatBuilder(assetData.created_by) },
    updated_at_ts: assetData.updated_at * 1000000,
    updated_by: isUserId(assetData.updated_by)
      ? usersData.find((user) => user.user_id === assetData.updated_by)
      : { name: formatBuilder(assetData.updated_by) },
  }))

  /** Effects */

  useDocumentTitle('DS Assets')

  /** Handlers */

  const handleCreateAssetModal = useCallback((value: boolean) => () => setIsCreateAssetModalOpen(value), [])

  const handleRedirect = useCallback((path: string) => navigate(path), [navigate])

  const handleRowClicked = useCallback(
    (event: RowClickedEvent) => {
      if (gridRef.current?.api && !isFocusedCellInteractive(event)) {
        if (event.node.data.id) {
          if (isTouchDown) {
            window.open(`/dynamic-screens/assets-management/${event.node.data.id}`, '_blank', 'rel=noopener noreferrer')
            setIsTouchDown(false)
          } else navigate(`/dynamic-screens/assets-management/${event.node.data.id}`)

          gridRef.current.api.deselectAll()
        }
      }
    },
    [isTouchDown, navigate, setIsTouchDown]
  )

  const handleSelectionChanged = useCallback((event: SelectionChangedEvent) => {
    const selectedRows = [...event.api.getSelectedNodes()]

    setSelectedRows(selectedRows.map((row) => row.data.id))
    setSelectedRowStatuses(selectedRows.map((row) => row.data.status))
  }, [])

  const handleAddPropertiesClose = () => setIsAddPropertiesModalOpen(false)

  const handleChangeStatusClose = () => setIsChangeStatusModalOpen(false)

  const handleEditStatusSuccess = () => {
    setIsChangeStatusModalOpen(false)
    gridRef.current?.api?.deselectAll()
    queryClient.invalidateQueries({ queryKey: assetsManagementKeys.list(queryParams) })
    displayToast('Statuses successfully updated', 'success')
  }

  const handleEditStatusFailure = () => {
    setIsChangeStatusModalOpen(false)
    displayToast('Statuses could not be updated', 'error')
  }

  const handleEditTagsSuccess = () => {
    setIsAddPropertiesModalOpen(false)
    gridRef.current?.api?.deselectAll()
    queryClient.invalidateQueries({ queryKey: assetsManagementKeys.list(queryParams) })
    displayToast('Tags successfully added', 'success')
  }

  const handleEditTagsFailure = () => {
    setIsAddPropertiesModalOpen(false)
    displayToast('Tags could not be added', 'error')
  }

  const handleCreateAssetSuccess = () => {
    setIsCreateAssetModalOpen(false)
    queryClient.invalidateQueries({ queryKey: assetsManagementKeys.list(queryParams) })
    displayToast('Asset successfully created', 'success')
  }

  const handleCreateAssetFailure = () => {
    setIsCreateAssetModalOpen(false)
    displayToast('Asset could not be created', 'error')
  }

  const handleUpdateAssetSuccess = () => {
    setIsUpdateAssetModalOpen(false)
    queryClient.invalidateQueries({ queryKey: assetsManagementKeys.list(queryParams) })
    setSelectedRows([])
    displayToast('Asset successfully updated', 'success')
  }

  const handleUpdateAssetFailure = () => {
    setIsUpdateAssetModalOpen(false)
    displayToast('Asset could not be updated', 'error')
  }

  const handleEditModalClose = () => {
    setAssetToEdit(undefined)
    setIsUpdateAssetModalOpen(false)
  }

  const handleEditAsset = (assetId: string) => {
    setAssetToEdit(assetId)
    setIsUpdateAssetModalOpen(true)
  }

  const handleTagKeyFilterChange = (option: IOption | null) => {
    setFilters((filters) => ({
      ...filters,
      [FILTERS.TAG_KEY]: option?.value || '',
    }))
  }

  const handleTagValueFilterChange = (evt: ChangeEvent<HTMLInputElement>) => {
    setFilters((filters) => ({
      ...filters,
      [FILTERS.TAG_VALUE]: evt.target.value,
    }))
  }

  const handleResetAll = () => setFilters(DEFAULT_FILTERS)

  const isUpdateModalOpen = isUpdateAssetModalOpen && (!!assetToEdit || !!(selectedRows.length > 0 && selectedRows[0]))

  return (
    <div className="assets-view">
      <ViewTitle className="top-zero layout-view-title">Assets management</ViewTitle>
      <Filters className="assets-view__filters">
        <FiltersContent>
          <SelectInput
            isClearable
            label="Property key"
            onChange={(option) => handleTagKeyFilterChange(option as IOption | null)}
            options={tagsKeyOptions}
            value={tagsKeyOptions.find(({ value }) => filters[FILTERS.TAG_KEY] === value) || ''}
          />
          <TextInput
            label="Property value"
            onChange={handleTagValueFilterChange}
            placeholder="Search"
            value={filters[FILTERS.TAG_VALUE]}
          />
          <Button className="reset-button" ghost onClick={handleResetAll}>
            Reset all
          </Button>
        </FiltersContent>
      </Filters>
      <Layout withPadding>
        <ContentLayout className="assets-view__content">
          <Section className="section">
            <ServerSideTableWrapper
              addButton
              addButtonDisabled={!assetsWriteAuthorized}
              addButtonLabel="New asset"
              addButtonTooltip={!assetsWriteAuthorized ? 'unauthorized' : undefined}
              className="assets-table with-min-height"
              columnDefs={columns}
              context={{
                handleEditAsset,
                handleRedirect,
              }}
              currentPage={page}
              editControls={
                selectedRows && selectedRows.length > 0 ? (
                  <EditControls
                    selectedRowStatuses={selectedRowStatuses}
                    selectedRows={selectedRows}
                    setIsAddPropertiesModalOpen={setIsAddPropertiesModalOpen}
                    setIsChangeStatusModalOpen={setIsChangeStatusModalOpen}
                    setIsUpdateAssetModalOpen={setIsUpdateAssetModalOpen}
                  />
                ) : null
              }
              entriesName={ENTRIES_NAME}
              loading={isAssetsLoading}
              onAddButtonClick={handleCreateAssetModal(true)}
              onRowClicked={handleRowClicked}
              onSelectionChanged={handleSelectionChanged}
              paginationApi={paginationApi}
              ref={gridRef}
              rowData={rowData || []}
              searchField="name"
              tableRef={tableRef}
              total={assetsData?.total || 0}
              withSearchBar
            />
          </Section>
          <CreateAssetModal
            isOpen={isCreateAssetModalOpen}
            onClose={handleCreateAssetModal(false)}
            onFailure={handleCreateAssetFailure}
            onSuccess={handleCreateAssetSuccess}
          />
          <EditAssetModal
            assetId={assetToEdit || selectedRows[0] || ''}
            isOpen={isUpdateModalOpen}
            onClose={handleEditModalClose}
            onFailure={handleUpdateAssetFailure}
            onSuccess={handleUpdateAssetSuccess}
          />
          <AddPropertiesModal
            isOpen={isAddPropertiesModalOpen}
            onClose={handleAddPropertiesClose}
            onFailure={handleEditTagsFailure}
            onSuccess={handleEditTagsSuccess}
            selectedAssetsIds={selectedRows}
          />
          <ChangeStatusModal
            isOpen={isChangeStatusModalOpen}
            onClose={handleChangeStatusClose}
            onFailure={handleEditStatusFailure}
            onSuccess={handleEditStatusSuccess}
            selectedAssetsIds={selectedRows}
          />
        </ContentLayout>
      </Layout>
    </div>
  )
}

export default AssetsListing
